import { useCallback, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UploadZone } from "@/components/generate/upload-zone";
import { AuthModal } from "@/components/ui/auth-modal";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { useGenerate } from "@/hooks/use-generation";
import { api } from "@/lib/api";
import { useUserStore } from "@/stores/user-store";
import { INTEGRATED_MODEL_ID, POWER_TIER_LABEL } from "@/config/models";
import { ArrowRight, Gift, Loader2, Sparkles, Volume2 } from "lucide-react";

const PROMO_ID = "free_h3_15s";
const ANIMATE_HREF = "/create/image-to-video";

interface PromoStatus {
  success: boolean;
  active: boolean;
  eligible: boolean;
  used: boolean;
  has_purchased?: boolean;
  reason: string | null;
  model_id: string;
  duration_seconds: number;
}

export function useFreeH3PromoStatus(userId?: string | null) {
  return useQuery({
    queryKey: ["promo", "free-h3-15s", userId ?? "anon"],
    queryFn: async () => {
      const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
      return api.get<PromoStatus>(`/promo/free-h3-15s${qs}`);
    },
    staleTime: 30_000,
  });
}

export function FreeGenPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isSignedIn } = useUser();
  const { hasPurchased } = useUserStore();
  const { data: promo, isLoading } = useFreeH3PromoStatus(user?.id);
  const generateMutation = useGenerate();

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const canGenerate = !!imageUrl && prompt.trim().length > 0;

  const handleGenerate = useCallback(async () => {
    if (!imageUrl || !prompt.trim()) return;
    try {
      const result = await generateMutation.mutateAsync({
        model_id: INTEGRATED_MODEL_ID,
        prompt: prompt.trim(),
        reference_image: imageUrl,
        duration_seconds: 15,
        promo: PROMO_ID,
      });
      await queryClient.invalidateQueries({ queryKey: ["promo", "free-h3-15s"] });
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) {
      console.error("Free generation failed:", err);
    }
  }, [imageUrl, prompt, generateMutation, navigate, queryClient]);

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-5 py-16 flex justify-center">
        <Loader2 className="size-6 animate-spin text-[#e8822e]" />
      </div>
    );
  }

  if (!promo?.active || promo.used || (isSignedIn && hasPurchased && !promo.eligible && promo.reason === "already_used")) {
    return <Navigate to="/" replace />;
  }

  if (isSignedIn && !hasPurchased) {
    return (
      <div className="max-w-lg mx-auto px-5 py-12 text-center space-y-4">
        <Gift className="size-10 text-[#e8822e] mx-auto" />
        <h1 className="text-2xl font-extrabold text-[#2d3127]">Purchasers only</h1>
        <p className="text-[14px] text-[#8a8570] leading-relaxed">
          This limited-time free 15s {POWER_TIER_LABEL} generation is for anyone who&apos;s bought a star pack.
        </p>
        <Link to="/billing" className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-xl text-[14px]">
          <Sparkles className="size-4" />
          Get Stars
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 py-8 space-y-6">
      <div className="rounded-2xl border-2 border-[#e8822e]/40 bg-gradient-to-br from-[#fff4e8] to-white p-5 space-y-2">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#e8822e] text-white text-[10px] font-bold uppercase tracking-wider">
          <Gift className="size-3" />
          Limited time
        </div>
        <h1 className="text-2xl font-extrabold text-[#2d3127] tracking-tight">
          Free Generation — 15 Second
        </h1>
        <p className="text-[13px] text-[#8a8570] leading-relaxed">
          One free {POWER_TIER_LABEL} video with native audio for purchasers. Upload an image, write a prompt, and go — 0 stars.
        </p>
        <p className="text-[12px] text-[#5a5545] font-medium flex items-center gap-1.5 pt-1">
          <Volume2 className="size-3.5 text-[#e8822e]" />
          Locked to {POWER_TIER_LABEL} · 15s · with sound
        </p>
      </div>

      <UploadZone imageUrl={imageUrl} onImageChange={setImageUrl} />

      <div>
        <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">
          Prompt
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Camera slowly pushes in as they turn and smile, soft wind in the hair, cinematic lighting..."
          rows={3}
          className="input-field resize-none"
        />
      </div>

      <Show when="signed-out">
        <button
          type="button"
          onClick={() => setShowAuth(true)}
          className="btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
        >
          <Sparkles className="size-4" />
          Sign Up to Claim
        </button>
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          title="Sign Up to Claim Free Gen"
        />
      </Show>

      <Show when="signed-in">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate || generateMutation.isPending}
          className="btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
        >
          {generateMutation.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Generating free 15s...
            </>
          ) : (
            <>
              <Gift className="size-4" />
              Generate Free 15s Video
            </>
          )}
        </button>
      </Show>

      {generateMutation.isError && (
        <NsfwOrError error={generateMutation.error} />
      )}

      <div className="rounded-xl border border-[rgba(45,49,39,0.08)] bg-white/70 p-4 text-center space-y-2">
        <p className="text-[13px] text-[#5a5545] leading-relaxed">
          Want more {POWER_TIER_LABEL} 15s videos? Generate them anytime on the Image to Video page (uses stars).
        </p>
        <Link
          to={ANIMATE_HREF}
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#e8822e] hover:underline"
        >
          Go to Image to Video
          <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
